<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useAccount } from "dashboard/composables/useAccount";
import ContactAttributesAPI from "dashboard/api/pipelines/contactAttributes";

const props = defineProps({
  contactId: { type: Number, required: true },
});

const { accountId } = useAccount();

const isLoading = ref(false);
const attributes = ref(null);

const loadData = async () => {
  isLoading.value = true;
  try {
    const res = await ContactAttributesAPI.getAttributes(
      accountId.value,
      props.contactId,
    );
    attributes.value = res.data?.data || null;
  } catch {
    attributes.value = null;
  } finally {
    isLoading.value = false;
  }
};

onMounted(loadData);
watch(() => props.contactId, loadData);

const firstTouch = computed(() => attributes.value?.first_touch || null);
const lastTouch = computed(() => attributes.value?.last_touch || null);
const origin = computed(() => attributes.value?.origin || {});
const ids = computed(() => attributes.value?.ids || {});

const UTM_FIELDS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
];
const ORIGIN_FIELDS = [
  "landing_page",
  "referrer",
  "first_seen_at",
  "last_seen_at",
];
const ID_FIELDS = [
  "gclid",
  "fbclid",
  "ttclid",
  "ctwa_clid",
  "dataset_id",
  "fbp",
  "ga_client_id",
  "session_id",
  "pixel_id",
];

const DATE_FIELDS = new Set(["first_seen_at", "last_seen_at"]);

const copiedKey = ref(null);

const copyValue = async (key, value) => {
  if (!value) return;
  try {
    await navigator.clipboard.writeText(value);
    copiedKey.value = key;
    setTimeout(() => {
      copiedKey.value = null;
    }, 1500);
  } catch (_e) {
    // clipboard write failed silently
  }
};

const formatDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  const months = [
    "jan",
    "fev",
    "mar",
    "abr",
    "mai",
    "jun",
    "jul",
    "ago",
    "set",
    "out",
    "nov",
    "dez",
  ];
  const time = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  return `${d.getDate()} de ${months[d.getMonth()]}. · ${time}`;
};

const displayValue = (field, value) => {
  if (!value) return null;
  return DATE_FIELDS.has(field) ? formatDate(value) : value;
};

const touchField = (touch, field) => touch?.[field] ?? null;
</script>

<template>
  <div class="flex flex-col gap-0">
    <!-- Header -->
    <div class="px-6 py-4 border-b border-n-weak">
      <h3 class="text-sm font-medium text-n-slate-12">
        {{ $t("CONTACT_METRICS.ATTRIBUTES.TITLE") }}
      </h3>
      <p class="text-xs text-n-slate-10 mt-0.5">
        {{ $t("CONTACT_METRICS.ATTRIBUTES.SUBTITLE") }}
      </p>
    </div>

    <!-- Loading -->
    <div
      v-if="isLoading"
      class="flex flex-col items-center justify-center py-16 gap-2"
    >
      <span class="i-lucide-loader-2 size-6 text-n-slate-10 animate-spin" />
    </div>

    <div v-else class="flex flex-col gap-4 p-4">
      <!-- UTM Parameters — first touch vs last touch -->
      <div
        class="m-0.5 rounded-xl shadow outline outline-1 outline-n-container bg-n-solid-2 overflow-hidden"
      >
        <div class="flex items-center gap-2 px-5 py-3 border-b border-n-weak">
          <span
            class="flex items-center justify-center w-6 h-6 rounded-md bg-n-blue-3"
          >
            <span class="i-lucide-tag size-3.5 text-n-blue-11" />
          </span>
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t("CONTACT_METRICS.ATTRIBUTES.SECTION_UTM") }}
          </span>
        </div>

        <div class="flex flex-col divide-y divide-n-weak">
          <!-- First touch -->
          <div>
            <div class="px-5 py-2 bg-n-solid-3 border-b border-n-weak">
              <span
                class="text-xs font-medium text-n-slate-10 uppercase tracking-wider flex items-center gap-1.5 flex-wrap"
              >
                <span class="size-1.5 rounded-full bg-n-blue-9 flex-shrink-0" />
                {{ $t("CONTACT_METRICS.ATTRIBUTES.FIRST_TOUCH") }}
                <span
                  v-if="firstTouch?.occurred_at"
                  class="text-n-slate-10 font-normal normal-case tracking-normal"
                >
                  · {{ formatDate(firstTouch.occurred_at) }}
                </span>
                <span
                  v-else
                  class="text-n-slate-10 font-normal normal-case tracking-normal"
                >
                  {{ $t("CONTACT_METRICS.ATTRIBUTES.NO_DATA") }}
                </span>
              </span>
            </div>
            <div class="divide-y divide-n-weak">
              <div
                v-for="field in UTM_FIELDS"
                :key="'ft-' + field"
                class="group flex items-center gap-3 px-5 py-2.5 hover:bg-n-alpha-1 transition-colors"
              >
                <span
                  class="text-xs text-n-slate-10 flex-shrink-0 w-24 truncate"
                >
                  {{ $t("CONTACT_METRICS.ATTRIBUTES.FIELDS." + field) }}
                </span>
                <span
                  class="text-xs flex-1 text-right"
                  :class="
                    touchField(firstTouch, field)
                      ? 'font-medium text-n-slate-12 min-w-0 truncate font-mono'
                      : 'text-n-slate-10'
                  "
                  >{{ touchField(firstTouch, field) || "—" }}</span
                >
                <button
                  v-if="touchField(firstTouch, field)"
                  class="copy-btn opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 p-1 rounded hover:bg-n-alpha-2"
                  @click="
                    copyValue('ft-' + field, touchField(firstTouch, field))
                  "
                >
                  <span
                    class="size-3"
                    :class="
                      copiedKey === 'ft-' + field
                        ? 'i-lucide-check text-n-teal-11'
                        : 'i-lucide-copy text-n-slate-10'
                    "
                  />
                </button>
                <span v-else class="flex-shrink-0 w-5" />
              </div>
            </div>
          </div>

          <!-- Last touch -->
          <div>
            <div class="px-5 py-2 bg-n-solid-3 border-b border-n-weak">
              <span
                class="text-xs font-medium text-n-slate-10 uppercase tracking-wider flex items-center gap-1.5 flex-wrap"
              >
                <span class="size-1.5 rounded-full bg-n-teal-9 flex-shrink-0" />
                {{ $t("CONTACT_METRICS.ATTRIBUTES.LAST_TOUCH") }}
                <span
                  v-if="lastTouch?.occurred_at"
                  class="text-n-slate-10 font-normal normal-case tracking-normal"
                >
                  · {{ formatDate(lastTouch.occurred_at) }}
                </span>
                <span
                  v-else
                  class="text-n-slate-10 font-normal normal-case tracking-normal"
                >
                  {{ $t("CONTACT_METRICS.ATTRIBUTES.NO_DATA") }}
                </span>
              </span>
            </div>
            <div class="divide-y divide-n-weak">
              <div
                v-for="field in UTM_FIELDS"
                :key="'lt-' + field"
                class="group flex items-center gap-3 px-5 py-2.5 hover:bg-n-alpha-1 transition-colors"
              >
                <span
                  class="text-xs text-n-slate-10 flex-shrink-0 w-24 truncate"
                >
                  {{ $t("CONTACT_METRICS.ATTRIBUTES.FIELDS." + field) }}
                </span>
                <span
                  class="text-xs flex-1 text-right"
                  :class="
                    touchField(lastTouch, field)
                      ? 'font-medium text-n-slate-12 min-w-0 truncate font-mono'
                      : 'text-n-slate-10'
                  "
                  >{{ touchField(lastTouch, field) || "—" }}</span
                >
                <button
                  v-if="touchField(lastTouch, field)"
                  class="copy-btn opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 p-1 rounded hover:bg-n-alpha-2"
                  @click="
                    copyValue('lt-' + field, touchField(lastTouch, field))
                  "
                >
                  <span
                    class="size-3"
                    :class="
                      copiedKey === 'lt-' + field
                        ? 'i-lucide-check text-n-teal-11'
                        : 'i-lucide-copy text-n-slate-10'
                    "
                  />
                </button>
                <span v-else class="flex-shrink-0 w-5" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Origem & Sessão -->
      <div
        class="m-0.5 rounded-xl shadow outline outline-1 outline-n-container bg-n-solid-2 overflow-hidden"
      >
        <div class="flex items-center gap-2 px-5 py-3 border-b border-n-weak">
          <span
            class="flex items-center justify-center w-6 h-6 rounded-md bg-n-iris-3"
          >
            <span class="i-lucide-globe size-3.5 text-n-iris-11" />
          </span>
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t("CONTACT_METRICS.ATTRIBUTES.SECTION_ORIGIN") }}
          </span>
        </div>
        <div class="divide-y divide-n-weak">
          <div
            v-for="field in ORIGIN_FIELDS"
            :key="'org-' + field"
            class="group flex items-center gap-3 px-5 py-3 hover:bg-n-alpha-1 transition-colors"
          >
            <span class="text-xs text-n-slate-10 flex-shrink-0 w-32">
              {{ $t("CONTACT_METRICS.ATTRIBUTES.FIELDS." + field) }}
            </span>
            <span
              class="text-xs flex-1 text-right"
              :class="
                displayValue(field, origin[field])
                  ? 'font-medium text-n-slate-12 min-w-0 truncate font-mono'
                  : 'text-n-slate-10'
              "
              >{{ displayValue(field, origin[field]) || "—" }}</span
            >
            <button
              v-if="origin[field] && !DATE_FIELDS.has(field)"
              class="copy-btn opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 p-1 rounded hover:bg-n-alpha-2"
              @click="copyValue('org-' + field, origin[field])"
            >
              <span
                class="size-3"
                :class="
                  copiedKey === 'org-' + field
                    ? 'i-lucide-check text-n-teal-11'
                    : 'i-lucide-copy text-n-slate-10'
                "
              />
            </button>
            <span v-else class="flex-shrink-0 w-5" />
          </div>
        </div>
      </div>

      <!-- IDs de Rastreamento -->
      <div
        class="m-0.5 rounded-xl shadow outline outline-1 outline-n-container bg-n-solid-2 overflow-hidden"
      >
        <div class="flex items-center gap-2 px-5 py-3 border-b border-n-weak">
          <span
            class="flex items-center justify-center w-6 h-6 rounded-md bg-n-amber-3"
          >
            <span class="i-lucide-fingerprint size-3.5 text-n-amber-11" />
          </span>
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t("CONTACT_METRICS.ATTRIBUTES.SECTION_IDS") }}
          </span>
        </div>
        <div class="divide-y divide-n-weak">
          <div
            v-for="field in ID_FIELDS"
            :key="'id-' + field"
            class="group flex items-center gap-3 px-5 py-3 hover:bg-n-alpha-1 transition-colors"
          >
            <span class="text-xs text-n-slate-10 flex-shrink-0 w-32">
              {{ $t("CONTACT_METRICS.ATTRIBUTES.FIELDS." + field) }}
            </span>
            <span
              class="text-xs flex-1 text-right"
              :class="
                ids[field]
                  ? 'font-medium text-n-slate-11 min-w-0 truncate font-mono'
                  : 'text-n-slate-10'
              "
              >{{ ids[field] || "—" }}</span
            >
            <button
              v-if="ids[field]"
              class="copy-btn opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 p-1 rounded hover:bg-n-alpha-2"
              @click="copyValue('id-' + field, ids[field])"
            >
              <span
                class="size-3"
                :class="
                  copiedKey === 'id-' + field
                    ? 'i-lucide-check text-n-teal-11'
                    : 'i-lucide-copy text-n-slate-10'
                "
              />
            </button>
            <span v-else class="flex-shrink-0 w-5" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Em dispositivos touch (sem hover real), sempre mostra o botão de copiar */
@media (hover: none) {
  .copy-btn {
    opacity: 1 !important;
  }
}
</style>
